<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.adminTasks') }}</h2>
      <el-button type="primary" class="btn-glass" @click="createDialog=true">{{ $t('admin.create') }}</el-button>
    </div>

    <div class="glass-card" style="margin-top:14px; padding:14px;">
      <el-table :data="tasks" style="width:100%;" @row-click="openReceipts">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" :label="$t('field.title')" />
        <el-table-column prop="status" :label="$t('field.status')" width="120">
          <template slot-scope="s">
            <span :class="['tag-pill', s.row.status === 'CLOSED' ? 'tag-pill--warn' : '']">
              {{ taskStatusText(s.row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" :label="$t('field.createdAt')" width="190">
          <template slot-scope="s">
            {{ s.row.createdAt ? new Date(s.row.createdAt).toLocaleString() : '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="220" class-name="actions-col">
          <template slot-scope="s">
            <el-badge :value="s.row.newSubmitted" :max="9" :hidden="(s.row.newSubmitted||0)<=0" class="badge-action">
              <el-button size="mini" @click.stop="openReceipts(s.row, $event)">{{ $t('admin.viewReceipts') }}</el-button>
            </el-badge>
            <el-button
              v-if="s.row.status !== 'CLOSED'"
              size="mini"
              type="warning"
              @click.stop="closeTask(s.row)"
            >{{ $t('admin.closeTask') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="$t('admin.create')"
      :visible.sync="createDialog"
      width="560px"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onCreateClose"
    >
      <el-form :model="createForm" label-position="top">
        <el-form-item :label="$t('field.title')">
          <el-input v-model="createForm.title" />
        </el-form-item>
        <el-form-item :label="$t('field.message')">
          <el-input v-model="createForm.content" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item :label="$t('admin.assignUsers')">
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;flex-wrap:wrap;">
            <el-button size="mini" @click="selectAllUsers">{{ $t('common.selectAll') }}</el-button>
            <el-button size="mini" @click="invertUsers">{{ $t('common.invert') }}</el-button>
            <el-button size="mini" @click="clearUsers">{{ $t('common.clear') }}</el-button>
            <span class="muted" style="font-size:12px;">{{ createForm.userIds.length }}/{{ users.length }}</span>
          </div>
          <el-select v-model="createForm.userIds" multiple filterable style="width: 100%;">
            <el-option v-for="u in users" :key="u.id" :value="u.id" :label="`${u.displayName} (${u.username})`" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('common.upload')">
          <FileUploader ref="createUploader" v-model="createFiles" @uploading-change="createUploading = $event" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="saving || createUploading"
          :disabled="saving || createUploading"
          @click="create"
        >{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>

    <el-drawer
      :title="$t('admin.viewReceipts')"
      :visible.sync="drawer"
      size="70%"
      :with-header="true"
      :modal="false"
      :append-to-body="false"
    >
      <div style="padding:16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ current ? current.title : '-' }}</div>
          <span class="tag-pill">#{{ current ? current.id : '-' }}</span>
        </div>
        <div class="muted" style="margin-top:6px;">{{ current ? (new Date(current.createdAt).toLocaleString()) : '-' }}</div>

        <div class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('field.message') }}</div>
          <div class="muted" v-if="!current || !current.content" style="margin-top:8px;">-</div>
          <div v-else style="margin-top:8px;white-space:pre-wrap;line-height:1.6;">{{ current.content }}</div>
        </div>

        <div v-if="current && current.fileIds && current.fileIds.length>0" class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('field.attachments') }}</div>
          <div style="margin-top:8px;">
            <AttachmentGallery :file-ids="current.fileIds" />
          </div>
        </div>

        <div class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('admin.viewReceipts') }}</div>
          <div v-if="loadingReceipts" class="muted" style="margin-top:8px;">{{ $t('common.loading') }}</div>
          <el-table v-else :data="receipts" style="width:100%; margin-top: 10px;">
            <el-table-column prop="displayName" :label="$t('admin.displayName')" width="180" show-overflow-tooltip />
            <el-table-column prop="receiptStatus" :label="$t('field.status')" width="120">
              <template slot-scope="s">
                <span class="tag-pill">{{ s.row ? $t(`status.task.${displayReceiptStatus(s.row)}`) : '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="submittedAt" :label="$t('common.submit')" width="180">
              <template slot-scope="s">
                {{ s.row.submittedAt ? new Date(s.row.submittedAt).toLocaleString() : '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.actions')" width="120">
              <template slot-scope="s">
                <el-button size="mini" @click="openReceiptDetail(s.row)">{{ $t('common.view') }}</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      :title="$t('admin.viewReceipts')"
      :visible.sync="receiptDetailDialog"
      width="640px"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
    >
      <div v-if="!receiptDetailTarget" class="muted">-</div>
      <div v-else>
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ receiptDetailTarget.displayName }} ({{ receiptDetailTarget.username }})</div>
          <span class="tag-pill">{{ $t(`status.task.${displayReceiptStatus(receiptDetailTarget)}`) }}</span>
        </div>
        <div class="muted" style="margin-top:6px;">
          {{ receiptDetailTarget.submittedAt ? new Date(receiptDetailTarget.submittedAt).toLocaleString() : '-' }}
        </div>

        <div class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('field.message') }}</div>
          <div class="muted" v-if="!receiptDetailTarget.content" style="margin-top:8px;">-</div>
          <div v-else style="margin-top:8px;white-space:pre-wrap;line-height:1.6;">{{ receiptDetailTarget.content }}</div>
        </div>

        <div v-if="receiptDetailTarget.fileIds && receiptDetailTarget.fileIds.length>0" class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('field.attachments') }}</div>
          <div style="margin-top:8px;">
            <AttachmentGallery :file-ids="receiptDetailTarget.fileIds" />
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="receiptDetailDialog=false">{{ $t('common.cancel') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { adminPageTasks, adminCreateTask, adminTaskReceipts, adminListUsers, adminMarkTaskSeen, adminCloseTask } from '../../api/admin';
import FileUploader from '../../components/FileUploader.vue';
import AttachmentGallery from '../../components/AttachmentGallery.vue';

export default {
  name: 'AdminTasks',
  components: { FileUploader, AttachmentGallery },
  data() {
    return {
      tasks: [],
      total: 0,
      page: 1,
      pageSize: 10,
      users: [],
      saving: false,
      createDialog: false,
      createForm: { title: '', content: '', userIds: [], fileIds: [] },
      createFiles: [],
      createUploading: false,
      drawer: false,
      current: null,
      receipts: [],
      loadingReceipts: false,
      receiptDetailDialog: false,
      receiptDetailTarget: null
    };
  },
  created() {
    this.load();
  },
  computed: {
    createFileIds() {
      return (this.createFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    badgeTotal() {
      return this.$store.state.adminBadges ? this.$store.state.adminBadges.tasks : 0;
    },
    version() {
      return this.$store.state.versions.tasks;
    }
  },
  watch: {
    version() {
      this.load();
    }
  },
  methods: {
    displayReceiptStatus(r) {
      if (!r) return 'UNREAD';
      if (r.receiptStatus === 'SUBMITTED') return 'SUBMITTED';
      if (r.confirmedAt) return 'CONFIRMED';
      return r.receiptStatus || 'UNREAD';
    },
    taskStatusText(status) {
      if (status === 'CLOSED') return this.$t('admin.taskClosed');
      return this.$t('admin.taskActive');
    },
    onCreateClose() {
      try {
        if (this.$refs.createUploader && this.$refs.createUploader.cancelUploads) this.$refs.createUploader.cancelUploads();
      } catch (e) {}
      this.createForm = { title: '', content: '', userIds: [], fileIds: [] };
      this.createFiles = [];
      this.createUploading = false;
    },
    async load() {
      const [paged, users] = await Promise.all([
        adminPageTasks({ page: this.page - 1, size: this.pageSize }),
        adminListUsers()
      ]);
      this.tasks = paged.items || [];
      this.total = paged.total || 0;
      this.users = (users || []).filter((u) => (u.role === 'USER' || u.role === 'SECONDARY_HANDLER') && !u.deletedAt);
      this.$store.dispatch('refreshAdminBadges');
    },
    onPageChange(p) {
      this.page = p;
      this.load();
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
      this.load();
    },
    async create() {
      if (!this.createForm.title || !this.createForm.content) return this.$message.error(this.$t('common.required'));
      if (!this.createForm.userIds || this.createForm.userIds.length === 0) return this.$message.error(this.$t('common.required'));
      try {
        if (this.$refs.createUploader && this.$refs.createUploader.isUploading && this.$refs.createUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.saving = true;
      try {
        await adminCreateTask({ ...this.createForm, fileIds: this.createFileIds });
        try {
          if (this.$refs.createUploader && this.$refs.createUploader.markCommitted) this.$refs.createUploader.markCommitted(this.createFileIds);
        } catch (e) {}
        this.createDialog = false;
        this.onCreateClose();
        this.page = 1;
        await this.load();
      } finally {
        this.saving = false;
      }
    },
    selectAllUsers() {
      this.createForm.userIds = this.users.map((u) => u.id);
    },
    invertUsers() {
      const chosen = new Set(this.createForm.userIds || []);
      this.createForm.userIds = this.users.filter((u) => !chosen.has(u.id)).map((u) => u.id);
    },
    clearUsers() {
      this.createForm.userIds = [];
    },
    async openReceipts(row, evt) {
      // Prevent ElementUI focus style from making the button look "stuck" after click.
      try {
        if (evt && evt.currentTarget && evt.currentTarget.blur) evt.currentTarget.blur();
      } catch (e) {}
      this.current = row;
      this.drawer = true;
      this.loadingReceipts = true;
      try {
        this.receipts = await adminTaskReceipts(row.id);
        await adminMarkTaskSeen(row.id);
        await this.$store.dispatch('refreshAdminBadges');
        await this.load();
      } finally {
        this.loadingReceipts = false;
      }
    },
    async openReceiptDetail(r) {
      this.receiptDetailTarget = r;
      this.receiptDetailDialog = true;
    },
    async closeTask(row) {
      if (!row || !row.id) return;
      try {
        await this.$confirm(this.$t('admin.closeTaskConfirm'), this.$t('admin.closeTask'), {
          type: 'warning',
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel')
        });
      } catch (e) {
        return;
      }
      await adminCloseTask(row.id);
      await this.load();
    }
  }
};
</script>

<style scoped>
</style>
